import { useRef, useState, useEffect } from "react";
import * as icons from "@tabler/icons-react";
import { useMantineColorScheme } from "@mantine/core";
import "./style.scss";
import PropTypes from "prop-types";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { useAtom } from "jotai";
export const AccordionItem = (props) => {
  // Update height if children/content changes while open

  const contentEl = useRef();
  const innerEl = useRef();
  const {
    header,
    children,
    openSteps,
    top,
    isSkeleton = false,
    isVisible,
    isSensitivity,
    id,
    childrenStyle = {},
  } = props;
  const [isOpen, setIsOpen] = useState(openSteps ?? props.open);
  // Whenever children change and accordion is open, recalculate height and set to auto after transition
  useEffect(() => {
    const container = contentEl.current;
    const inner = innerEl.current;
    if (!container || !inner) return;
    if (isOpen) {
      // Always set to pixel height when opening
      container.style.height = inner.scrollHeight + 'px';
      // After transition, set to auto for future content changes
      if (isSensitivity ?? true) {
        // Do not set to auto after expand; keep height in px to avoid reopening issue
      }
    }
    // --- ResizeObserver for dynamic content (e.g. charts) ---
    let resizeObs;
    if (isOpen && inner) {
      resizeObs = new window.ResizeObserver(() => {
        if (!container || !inner) return;
        // Only animate if not already auto
        if (container.style.height !== 'auto') {
          container.style.height = inner.scrollHeight + 'px';
        } else {
          // If auto, temporarily set to px to animate
          container.style.height = inner.scrollHeight + 'px';
          setTimeout(() => {
            container.style.height = 'auto';
          }, 350);
        }
      });
      resizeObs.observe(inner);
    }
    return () => {
      if (resizeObs && inner) resizeObs.unobserve(inner);
    };
  }, [children, isOpen]);
  useEffect(() => {
    if (openSteps !== undefined) {
      setIsOpen(openSteps);
    } else {
      setIsOpen(props.open ?? true);
    }
  }, [openSteps, props.open]);
  const handleToggle = () => {
    // If parent is controlling open state via openSteps prop, notify parent instead
    if (openSteps !== undefined && typeof props.onToggle === 'function') {
      props.onToggle(id);
      return;
    }
    setIsOpen((prevOpen) => !prevOpen);
  };
  const { colorScheme } = useMantineColorScheme();

  const accordianBodyWidth = "109vw";

  // Smooth height animation logic (improves initial mount & prevents mid-freeze)
  useEffect(() => {
    const container = contentEl.current;
    const inner = innerEl.current;
    if (!container || !inner) return;

    const duration = 350; // ms
    container.style.willChange = 'height';
    container.style.transition = `height ${duration}ms ease`;
    container.style.overflow = isOpen ? (isVisible ? 'visible' : 'hidden') : 'hidden';

    function setToAutoAfterTransition(e) {
      if (e && e.target !== container) return;
      if (!isOpen) return; // only set auto on expand
      // Only set to auto if still open
      container.style.height = 'auto';
      container.removeEventListener('transitionend', setToAutoAfterTransition);
    }

    if (isOpen) {
      // Starting point: if height is auto (from previous expand) set to its pixel height first
      container.style.height = inner.scrollHeight + 'px';
      // Force reflow to recognize current height
      void container.offsetHeight;
      // Collapse instantly to 0 then expand in next frame if this is the FIRST mount open (height 0 / not yet measured)
      const currentPx = container.getBoundingClientRect().height;
      if (currentPx === 0) {
        container.style.height = '0px';
        requestAnimationFrame(() => {
          container.style.height = inner.scrollHeight + 'px';
        });
      } else {
        // Normal toggle open: set from current (maybe stale) to new scrollHeight
        container.style.height = inner.scrollHeight + 'px';
      }
      container.addEventListener('transitionend', setToAutoAfterTransition);
    } else {
      // Closing: if height is auto we need pixel value first
      if (container.style.height === 'auto' || !container.style.height) {
        container.style.height = inner.scrollHeight + 'px';
        void container.offsetHeight; // reflow
      }
      requestAnimationFrame(() => {
        container.style.height = '0px';
      });
    }
    return () => container.removeEventListener('transitionend', setToAutoAfterTransition);
  }, [isOpen, isVisible]);

  return (
    <div
      className={`rc-accordion-card`}
      style={{
        border:
          colorScheme === "dark" ? "1px solid #505559" : "1px solid  #ddd",
      }}
    >
      <div
        className={`rc-accordion-header ${top ? "header-top" : ""}`}
        style={{
          border:
            colorScheme === "dark" ? "1px solid #505559" : "1px solid  #ebedf0",
        }}
      >
        <div
          className={`rc-accordion-toggle p-2 ${isOpen ? "active" : ""}  ${
            isSkeleton ? "accordian-skeleton" : " "
          }`}
          style={{
            backgroundColor: colorScheme === "dark" ? "#505559" : "#ebedf0",
          }}
          onClick={handleToggle}
          onKeyDown={() => {}}
        >
          <span className="rc-accordion-icon">
            <icons.IconCaretDownFilled
              color={colorScheme === "dark" ? "#d5d7e0" : "#475f7b"}
            />
          </span>
          {isSkeleton ? (
            header
          ) : (
            <h5 className="rc-accordion-title">{header}</h5>
          )}
        </div>
      </div>
      <div
        ref={contentEl}
        className={`rc-collapse ${isOpen ? "show" : ""}`}
        style={{
          transition: "height 0.35s ease",
          overflow: isOpen ? (isVisible ? "visible" : "hidden") : "hidden",
        }}
      >
        <div
          ref={innerEl}
          className="rc-accordion-body"
          style={{
            width: isSensitivity ? accordianBodyWidth : "",
            transition: "width 0.3s ease",
            // willChange: 'transform, opacity',
            ...childrenStyle,
          }}
        >
          <p className="mb-0">{children}</p>
        </div>
      </div>
    </div>
  );
};

AccordionItem.propTypes = {
  header: PropTypes.string.isRequired,
  children: PropTypes.node,
  openSteps: PropTypes.bool,
  height: PropTypes.string,
  top: PropTypes.bool,
  isSkeleton: PropTypes.bool,
  isVisible: PropTypes.bool,
  isSensitivity: PropTypes.bool,
  open: PropTypes.bool,
  childrenStyle: PropTypes.object,
};
